import { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Gift, Trash2, Loader2, Mail, Upload, CheckCircle2, Clock, Download, Search, RotateCcw } from "lucide-react";

export const FreeAccessManager = () => {
  const [list, setList] = useState([]);
  const [revocations, setRevocations] = useState([]);
  const [emails, setEmails] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data: l }, { data: r }] = await Promise.all([
        api.get("/admin/free-access"),
        api.get("/admin/free-access/revocations"),
      ]);
      setList(l);
      setRevocations(r || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitEmails = async (text) => {
    if (!text || !text.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/admin/free-access", { emails: text });
      const welcomed = data.welcomed ? ` ${data.welcomed} e-mail(s) de bienvenue envoyé(s).` : "";
      const ignored = data.invalid.length ? ` ${data.invalid.length} ignoré(s).` : "";
      toast.success(`${data.added.length} e-mail(s) ajouté(s).${welcomed}${ignored}`);
      setEmails("");
      load();
    } catch {
      toast.error("Ajout impossible.");
    } finally {
      setSaving(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    await submitEmails(text);
    if (fileRef.current) fileRef.current.value = "";
  };

  const remove = async (email) => {
    try {
      await api.delete(`/admin/free-access`, { params: { email } });
      toast.success("Accès retiré.");
      load();
    } catch {
      toast.error("Suppression impossible.");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/free-access/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "acces-gratuits.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export impossible.");
    }
  };

  const exportRevocations = async () => {
    try {
      const res = await api.get("/admin/free-access/revocations/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "revocations.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export impossible.");
    }
  };

  const restore = async (email) => {
    await submitEmails(email);
  };

  const filteredList = list.filter((x) => x.email.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <Card className="border-gray-200 mb-8" data-testid="free-access-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
            <Gift className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h2 className="font-serif-display text-lg font-normal text-gray-800">Accès gratuit à vie</h2>
            <p className="text-sm text-gray-500">Clients ayant acheté sur passerelle.site — accès offert sans paiement.</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3" data-testid="free-access-stats">
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-center">
            <p className="text-2xl font-extrabold text-emerald-700" data-testid="stat-total">{list.length}</p>
            <p className="text-xs text-gray-500">Accès offerts</p>
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-center">
            <p className="text-2xl font-extrabold text-emerald-700" data-testid="stat-accounts">{list.filter((x) => x.has_account).length}</p>
            <p className="text-xs text-gray-500">Comptes créés</p>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-center">
            <p className="text-2xl font-extrabold text-amber-600" data-testid="stat-pending">{list.filter((x) => !x.has_account).length}</p>
            <p className="text-xs text-gray-500">En attente</p>
          </div>
        </div>

        <div className="mt-4">
          <Textarea
            value={emails}
            onChange={(e) => setEmails(e.target.value)}
            placeholder="Collez une liste d'e-mails (séparés par des virgules, espaces ou retours à la ligne)&#10;client1@mail.com, client2@mail.com"
            className="min-h-[90px]"
            data-testid="free-access-textarea"
          />
          <div className="mt-3 flex flex-wrap gap-3">
            <Button onClick={() => submitEmails(emails)} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="free-access-add-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Accorder l'accès gratuit"}
            </Button>
            <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" data-testid="free-access-file-input" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={saving} className="rounded-xl" data-testid="free-access-import-button">
              <Upload className="w-4 h-4 mr-1" /> Importer un CSV
            </Button>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-gray-700" data-testid="free-access-count">
              {list.length} bénéficiaire(s)
            </p>
            {list.length > 0 && (
              <Button variant="outline" size="sm" onClick={exportCsv} className="rounded-lg" data-testid="free-access-export-button">
                <Download className="w-4 h-4 mr-1" /> Exporter CSV
              </Button>
            )}
          </div>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-emerald-700" />
          ) : list.length === 0 ? (
            <p className="text-sm text-gray-400">Aucun e-mail pour l'instant.</p>
          ) : (
            <>
              {list.length > 3 && (
                <div className="relative mb-3">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Rechercher un e-mail…"
                    className="pl-9 h-10"
                    data-testid="free-access-search"
                  />
                </div>
              )}
              {filteredList.length === 0 ? (
                <p className="text-sm text-gray-400 py-2" data-testid="free-access-search-empty">Aucun e-mail ne correspond.</p>
              ) : (
                <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto border border-gray-100 rounded-xl">
                  {filteredList.map((x) => (
                    <li key={x.email} className="flex items-center gap-3 px-4 py-2.5" data-testid="free-access-row">
                      <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm text-gray-700 truncate block">{x.email}</span>
                        <span className="text-xs text-gray-400" data-testid="free-access-source">{x.source}</span>
                      </div>
                      {x.has_account ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="free-access-status">
                          <CheckCircle2 className="w-3 h-3" /> Compte créé
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5" data-testid="free-access-status">
                          <Clock className="w-3 h-3" /> En attente
                        </span>
                      )}
                      <button
                        onClick={() => remove(x.email)}
                        className="ml-auto shrink-0 text-red-500 hover:text-red-700"
                        title="Retirer"
                        data-testid={`free-access-remove-${x.email}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        {revocations.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-100" data-testid="revocations-history">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Historique des révocations ({revocations.length})</p>
              <Button variant="outline" size="sm" onClick={exportRevocations} className="rounded-lg" data-testid="revocations-export-button">
                <Download className="w-4 h-4 mr-1" /> Exporter CSV
              </Button>
            </div>
            <ul className="divide-y divide-gray-100 max-h-56 overflow-y-auto border border-gray-100 rounded-xl">
              {revocations.map((r, i) => (
                <li key={`${r.email}-${i}`} className="flex items-center gap-3 px-4 py-2.5" data-testid="revocation-row">
                  <span className="text-sm text-gray-600 truncate">{r.email}</span>
                  <span className="text-xs text-gray-400">
                    {r.by === "passerelle.site" ? "Remboursement passerelle.site" : "Retiré manuellement"}
                  </span>
                  <span className="ml-auto shrink-0 text-xs text-gray-400">
                    {r.revoked_at ? new Date(r.revoked_at).toLocaleDateString("fr-FR") : ""}
                  </span>
                  <button
                    onClick={() => restore(r.email)}
                    disabled={saving}
                    className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 disabled:opacity-50"
                    title="Restaurer l'accès"
                    data-testid={`revocation-restore-${r.email}`}
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Restaurer
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
